import { Fragment, useState } from "react";
import { api } from "../api";
import { useProgress } from "../progress";
import { usePoll, ErrorBanner, Loading, Section, ago, when, minutesSince, useToast, Modal } from "./common";

const ACTIONS = [
  { type: "scan", label: "Scan complet", hint: "ingestion des marchés + analyse IA", primary: true },
  { type: "ingest", label: "Ingestion", hint: "rafraîchit les marchés Polymarket" },
  { type: "settle", label: "Règlement", hint: "règle les paris résolus" },
  { type: "wallets", label: "Smart money", hint: "met à jour les parieurs suivis" },
  { type: "learn", label: "Revue d'apprentissage", hint: "post-mortem et recommandations" },
  { type: "backtest", label: "Backtest", hint: "calibration du marché (validation)" },
];

function Health({ st, health }) {
  const scanMin = st?.lastScanAt ? minutesSince(st.lastScanAt) : null;
  const pwMin = st?.lastPricewatchAt ? minutesSince(st.lastPricewatchAt) : null;
  const okScan = scanMin != null && scanMin <= 45, okPw = pwMin != null && pwMin <= 6;
  const geo = st?.geo;
  const ai = health?.ai;
  const Item = ({ ok, label, value }) => (
    <div className="status-item"><span className={"dot " + (ok === true ? "dot-up" : ok === false ? "dot-down" : "dot-warn")} /><span>{label} <b>{value}</b></span></div>
  );
  return (
    <div className="status-row">
      <Item ok={health ? health.ok : null} label="Service" value={health ? (health.ok ? "en ligne" : (health.reasons || []).join(", ") || "dégradé") : "…"} />
      <Item ok={okScan} label="Dernier scan" value={scanMin == null ? "jamais" : ago(st.lastScanAt)} />
      <Item ok={okPw} label="Veille des prix" value={pwMin == null ? "—" : ago(st.lastPricewatchAt)} />
      <Item ok={ai ? !ai.open : null} label="IA" value={ai ? (ai.open ? `coupée (${ai.cause || "panne"})` : "disponible") : "…"} />
      <Item ok={geo ? geo.blocked === false : null} label="Sortie réseau" value={geo ? (geo.blocked == null ? "inconnue" : geo.blocked ? `BLOQUÉE (${geo.country})` : `${geo.country} · autorisée`) : "…"} />
      <Item ok={st?.copyLane ? (st.copyLane.enabled && !st.copyLane.cutAt) : null} label="Voie copie" value={st?.copyLane ? (!st.copyLane.enabled ? "désactivée" : st.copyLane.cutAt ? "coupée" : "active") : "…"} />
    </div>
  );
}

export default function System() {
  const { data: st, err, refresh } = usePoll(() => api("/agents/status"), 10000);
  const { data: health } = usePoll(() => fetch("/api/health").then((r) => r.json()), 15000);
  const { data: runsD, refresh: refreshRuns } = usePoll(() => api("/agents/runs"), 8000);
  const [openRun, setOpenRun] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const prog = useProgress();
  const toast = useToast();

  function trigger(a) {
    prog.run({ path: "/agents/run", body: a.type === "scan" ? { type: "scan", maxDeep: 6 } : { type: a.type }, label: a.label, onDone: () => { refresh(); refreshRuns(); }, onError: (m) => toast(m, "down") });
  }
  async function clearRuns() {
    try { await api("/agents/runs/clear", { method: "POST" }); setOpenRun(null); refreshRuns(); toast("Historique vidé."); }
    catch (e) { toast(e.message, "down"); }
    setConfirmClear(false);
  }

  if (!st && !err) return <Loading />;
  const cl = st?.copyLane;
  const runs = runsD?.runs || [];

  return (
    <div>
      <div className="page-head">
        <div><h1>Système</h1><div className="sub">Santé des boucles automatiques, voie copie, exécutions récentes.</div></div>
        <div className="page-actions">
          {ACTIONS.map((a) => <button key={a.type} className={"btn btn-sm " + (a.primary ? "btn-primary" : "btn-ghost")} title={a.hint} onClick={() => trigger(a)}>{a.label}</button>)}
        </div>
      </div>
      <ErrorBanner err={err} />
      <Health st={st} health={health} />

      {st?.running?.length > 0 && (
        <div className="notice" style={{ marginBottom: 18 }}>
          {st.running.length} tâche{st.running.length > 1 ? "s" : ""} en cours : {st.running.map((j) => j.label || j.type).join(" · ")}
        </div>
      )}

      {cl && (
        <Section title="Voie copie" hint={cl.cutAt ? `coupée le ${when(cl.cutAt)} — réarmement automatique quand le ROI mesuré des wallets repasse positif` : "reproduit les paris des meilleurs parieurs Polymarket, à la même part de capital"}>
          <div className="two-col">
            <div className="card table-wrap">
              <table className="table">
                <thead><tr><th>Parieur</th><th className="right">ROI</th><th className="right">P&L</th><th className="right">Capital</th><th>Dernier marché vu</th></tr></thead>
                <tbody>
                  {cl.wallets.length === 0 && <tr><td colSpan={5} className="table-empty">Aucun parieur suivi — lancez « Smart money ».</td></tr>}
                  {cl.wallets.map((w) => (
                    <tr key={w.wallet} style={{ opacity: w.followed ? 1 : 0.55 }}>
                      <td><span className="mono small" title={w.wallet}>{w.pseudonym || w.wallet.slice(0, 8) + "…" + w.wallet.slice(-4)}</span>{w.followed && <span className="badge badge-accent" style={{ marginLeft: 6 }}>copié</span>}</td>
                      <td className={"right mono " + ((w.roi || 0) >= 0 ? "up" : "down")}>{w.roi != null && Number.isFinite(Number(w.roi)) ? (w.roi * 100).toFixed(0) + " %" : "—"}</td>
                      <td className={"right mono " + ((w.pnl || 0) >= 0 ? "up" : "down")}>{w.pnl != null ? (w.pnl >= 0 ? "+" : "") + Math.round(w.pnl) + " $" : "—"}</td>
                      <td className="right mono muted">{w.capitalUsd ? Math.round(w.capitalUsd).toLocaleString("fr-FR") + " $" : "—"}</td>
                      <td className="muted small ellipsis" style={{ maxWidth: 220 }}>{w.lastSeenMarket || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="card card-pad">
              <dl className="kv" style={{ gap: "8px 14px" }}>
                <dt>Copies ouvertes</dt><dd className="mono">{cl.open} <span className="muted">/ {cl.maxOpen}</span></dd>
                <dt>Copies sur 24 h</dt><dd className="mono">{cl.placed24h} <span className="muted">/ {cl.maxPerDay}</span></dd>
                <dt>Positions totales</dt><dd className="mono">{cl.openAll} <span className="muted">/ {cl.maxOpenAll}</span></dd>
                <dt>En cours de match</dt><dd>{cl.allowInPlay ? `autorisé · ordre limite ${cl.inPlayGtcMin} min` : "non"}</dd>
                <dt>Sorties miroir</dt><dd>{cl.mirrorExits ? "oui (on vend quand le parieur vend)" : "non"}</dd>
                <dt>Parieurs copiés</dt><dd>{cl.wallets.filter((w) => w.followed).length} <span className="muted">sur {cl.wallets.length} suivis</span></dd>
              </dl>
            </div>
          </div>
        </Section>
      )}

      <Section title="Exécutions récentes" hint="cliquez un scan pour voir ses décisions" action={<button className="btn btn-quiet btn-sm" onClick={() => setConfirmClear(true)} disabled={!runs.length}>Vider l'historique</button>}>
        <div className="card table-wrap">
          <table className="table">
            <thead><tr><th>Tâche</th><th>État</th><th>Détail</th><th>Résultat</th><th className="right">Quand</th></tr></thead>
            <tbody>
              {runs.length === 0 && <tr><td colSpan={5} className="table-empty">Aucune exécution pour l'instant.</td></tr>}
              {runs.map((r) => {
                const steps = getSteps(r.result);
                const expanded = openRun === r._id;
                return (
                  <Fragment key={r._id}>
                    <tr className={steps.length ? "clickable" : ""} onClick={() => steps.length && setOpenRun(expanded ? null : r._id)}>
                      <td>{steps.length ? (expanded ? "▾ " : "▸ ") : ""}{r.label || r.type}</td>
                      <td><span className={"dot " + ({ running: "dot-accent dot-live", done: "dot-up", error: "dot-down", cancelled: "dot-warn" }[r.status] || "")} /> <span className="small" style={{ marginLeft: 4 }}>{{ queued: "en file", running: "en cours", done: "terminé", error: "erreur", cancelled: "annulé" }[r.status] || r.status}</span></td>
                      <td className="muted small" style={{ maxWidth: 300 }}>{r.error || r.progressMsg}</td>
                      <td className="mono small">{summarize(r.result)}</td>
                      <td className="right muted tiny nowrap">{when(r.createdAt)}</td>
                    </tr>
                    {expanded && <tr className="detail"><td colSpan={5} style={{ padding: 0 }}><StepsTable steps={steps} /></td></tr>}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>
      {confirmClear && <Modal title="Vider l'historique ?" onCancel={() => setConfirmClear(false)} onConfirm={clearRuns} confirmLabel="Vider" danger>Les exécutions terminées sont effacées ; celles en cours sont conservées.</Modal>}
    </div>
  );
}

function getSteps(result) {
  if (!result) return [];
  const p = result.pipeline || result;
  return Array.isArray(p.steps) ? p.steps : [];
}
function verdictBadge(v) {
  const s = String(v || "");
  if (s.includes("approved") || s === "placed") return "badge-up";
  if (s.includes("pending")) return "badge-warn";
  if (s.includes("rejeté") || s.includes("rejected") || s.includes("erreur")) return "badge-down";
  return "";
}
function StepsTable({ steps }) {
  return (
    <table className="table job-steps">
      <thead><tr><th>Marché</th><th>Issue</th><th className="right">Edge</th><th className="right">EV</th><th className="right">Conf.</th><th>Verdict</th><th>Raison</th></tr></thead>
      <tbody>
        {steps.map((s, i) => (
          <tr key={i}>
            <td style={{ maxWidth: 240 }}>{(s.question || "").slice(0, 60)}</td>
            <td>{s.outcome || "—"}</td>
            <td className={"right mono " + ((s.edgePct || 0) >= 0 ? "up" : "down")}>{s.edgePct != null ? s.edgePct + " %" : "—"}</td>
            <td className="right mono">{s.evPct != null ? s.evPct + " %" : "—"}</td>
            <td className="right mono">{s.confidence != null ? (s.confidence * 100).toFixed(0) + " %" : "—"}</td>
            <td><span className={"badge " + verdictBadge(s.verdict)}>{s.verdict || "?"}</span></td>
            <td className="muted" style={{ maxWidth: 280 }}>{s.reason || ""}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
function summarize(result) {
  if (!result) return "—";
  const p = result.pipeline || result;
  if (p.proposed != null) return `${p.proposed} placés · ${p.analyzed} analysés · ${p.skipped} écartés`;
  if (result.upserted != null) return `${result.upserted} marchés`;
  if (result.settled != null) return `${result.settled} réglés`;
  if (result.totalWatchlist != null) return `${result.watchlistedNow}/${result.tracked} suivis`;
  if (result.sampleSize != null) return `n=${result.sampleSize}`;
  return "ok";
}
